import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as EventoMouse, type ReactNode } from 'react';
import { animate, AnimatePresence, motion } from 'motion/react';
import { aparecer, deslizar, pop, resorte, revelarDesde, subir, suave, abrir } from './movimiento';
import { api, type Hallazgo, type Notificacion, type Notificaciones, type Perfil } from '../api';
import { aviso, botonRedondo, botonSecundario, c, miles, mono, tono, tonoDeTexto } from '../theme';
import { useEsCelular } from '../lib/useMedia';
import { Icono, Marca, PanelError } from './ui';
import Escaner, { ResultadoEscaneo } from './Escaner';
import { mensajeDeError, useEscape, useLatido, VIVO } from '../lib/useApi';

export type Vista =
  | 'dashboard'
  | 'resumen'
  | 'facturacion'
  | 'stock'
  | 'contenedores'
  | 'solicitud'
  | 'armado'
  | 'frac'
  | 'correo'
  | 'racks'
  | 'papeles'
  | 'ubicar'
  | 'operar'
  | 'etiquetasUbicacion'
  | 'etiquetasPallet'
  | 'estadias'
  | 'inventario'
  | 'archivos'
  | 'planta'
  | 'articulos'
  | 'etiquetas'
  | 'manual'
  | 'remitos'
  | 'mantenimiento'
  | 'usuarios'
  | 'palletsEliminados'
  | 'contenedor'
  | 'vaciarRack';

export const TITULOS: Record<Vista, string> = {
  dashboard: 'Inicio',
  resumen: 'Resumen',
  facturacion: 'Simulador de facturación',
  stock: 'Stock disponible',
  contenedores: 'Nuevo contenedor',
  solicitud: 'Solicitar OT',
  armado: 'Armado de pallets',
  frac: 'Órdenes de trabajo',
  correo: 'Pedidos por mail',
  racks: 'Mapa del depósito',
  papeles: 'Contenedores',
  ubicar: 'Ubicar pallets',
  operar: 'Operar',
  // Las tres comparten título: al cambiar de pestaña el encabezado no salta.
  etiquetasUbicacion: 'Imprimir etiquetas',
  etiquetasPallet: 'Imprimir etiquetas',
  estadias: 'Permanencia actual',
  inventario: 'Cargar inventario',
  archivos: 'Archivos compartidos',
  planta: 'Planta del depósito',
  articulos: 'Artículos',
  etiquetas: 'Diseño de etiquetas',
  manual: 'Imprimir etiquetas',
  remitos: 'Remitos',
  mantenimiento: 'Mantenimiento',
  usuarios: 'Usuarios y permisos',
  palletsEliminados: 'Pallets eliminados',
  contenedor: 'Contenedor',
  vaciarRack: 'Vaciar rack',
};

/** `tambien`: otras vistas que dejan la entrada resaltada (las pestañas de un mismo menú, la ficha). */
type ItemNav = { vista: Vista; label: string; icono: string; badge?: string; tambien?: Vista[] };

const VISTAS_ETIQUETAS: Vista[] = ['etiquetasPallet', 'etiquetasUbicacion', 'manual'];

type Grupo = { titulo: string; items: ItemNav[] };

function estiloItem(activo: boolean) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 11px',
    borderRadius: 12,
    border: 0,
    // El fondo verde y la barrita de la izquierda los pone `Resaltado`, que
    // viaja de un ítem al otro; el borde transparente guarda su lugar.
    borderLeft: '3px solid transparent',
    background: 'transparent',
    color: activo ? c.verde : c.texto2,
    fontSize: 13.5,
    fontWeight: activo ? 600 : 500,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left' as const,
    position: 'relative' as const,
    isolation: 'isolate' as const,
  };
}

/**
 * El ítem que tenía el resaltado en cada menú (barra o cajón). Se guarda el
 * elemento y se mide en el momento del cambio: una posición guardada de antes
 * puede haber quedado vieja.
 */
const ultimoItem: Record<string, HTMLElement> = {};

/**
 * El resaltado del ítem elegido: nace en el ítem nuevo pero arranca corrido
 * a donde estaba el anterior, y se desliza hasta su lugar.
 *
 * No es un `layoutId`: la barra lateral está pegada arriba y Motion la medía
 * contra la página, así que al cambiar de pantalla con la página scrolleada
 * el resaltado volaba desde dos mil píxeles más abajo. `offsetTop` se mide
 * dentro del menú y el scroll de la página no lo toca.
 */
function Resaltado({ lugar }: { lugar: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const item = el?.parentElement;
    if (!el || !item) return;
    // El «desde» queda guardado en el elemento: React (en desarrollo) corre el
    // efecto dos veces y la segunda ya encontraba la posición nueva y no animaba.
    const previo = ultimoItem[lugar];
    const guardado = el.dataset.desde;
    const antes =
      guardado !== undefined
        ? Number(guardado)
        : previo && previo !== item && previo.isConnected
          ? previo.offsetTop
          : undefined;
    el.dataset.desde = String(antes ?? item.offsetTop);
    ultimoItem[lugar] = item;
    if (antes === undefined || antes === item.offsetTop) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const control = animate(el, { y: [antes - item.offsetTop, 0] }, resorte);
    return () => control.stop();
  }, [lugar]);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 12,
        background: c.verdeSuave,
        borderLeft: `3px solid ${c.verde}`,
        zIndex: -1,
      }}
    />
  );
}

function estiloBadge(activo: boolean) {
  return {
    flex: '0 0 auto',
    whiteSpace: 'nowrap' as const,
    minWidth: 22,
    padding: '1px 7px',
    borderRadius: 999,
    background: activo ? '#fff' : c.fondo,
    border: `1px solid ${activo ? c.verde : c.borde}`,
    fontSize: 12,
    fontWeight: 600,
    color: activo ? c.verde : c.texto3,
    textAlign: 'center' as const,
  };
}

// La barra lateral de la PC y el cajón del celular muestran lo mismo: un
// único recorrido evita que se desarmen entre sí con el tiempo.
function listaGrupos(grupos: Grupo[], vistaActual: Vista, onElegir: (v: Vista) => void, lugar: 'barra' | 'cajon') {
  return grupos.map((g, gi) => (
    <div key={g.titulo} style={{ display: 'contents' }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: c.texto3,
          padding: gi === 0 ? '8px 12px 4px' : '14px 12px 4px',
        }}
      >
        {g.titulo}
      </span>
      {g.items.map((it) => {
        const activo = vistaActual === it.vista || !!it.tambien?.includes(vistaActual);
        return (
          <button
            key={it.vista}
            type="button"
            onClick={() => onElegir(it.vista)}
            aria-current={activo ? 'page' : undefined}
            style={estiloItem(activo)}
          >
            {activo && <Resaltado lugar={lugar} />}
            <Icono nombre={it.icono} size={20} />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {it.label}
            </span>
            {it.badge && <span style={estiloBadge(activo)}>{it.badge}</span>}
          </button>
        );
      })}
    </div>
  ));
}

function PanelNotificaciones({
  datos,
  error,
  onIr,
  onLeerTodas,
  onReintentar,
}: {
  datos: Notificaciones | null;
  error: boolean;
  onIr: (n: Notificacion) => void;
  onLeerTodas: () => void;
  onReintentar: () => void;
}) {
  return (
    <motion.div
      {...revelarDesde('calc(100% - 19px)', '-27px')}
      style={{
        transformOrigin: 'top right',
        position: 'absolute',
        top: 46,
        right: 0,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        background: c.panel,
        border: `1px solid ${c.borde}`,
        borderRadius: 16,
        boxShadow: '0 12px 32px rgba(26,26,26,.14)',
        zIndex: 20,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: c.texto, flex: 1 }}>Notificaciones</span>
        <button
          type="button"
          onClick={onLeerTodas}
          style={{ border: 0, background: 'transparent', color: c.verde, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
        >
          Marcar todas como leídas
        </button>
      </div>

      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {error && (
          <div style={{ padding: '28px 20px', textAlign: 'center', fontSize: 13, color: c.texto2 }}>
            <p style={{ margin: '0 0 12px' }}>No se pudieron cargar.</p>
            <button
              type="button"
              onClick={onReintentar}
              style={{
                border: `1px solid ${c.borde}`,
                background: c.panel,
                color: c.verde,
                fontWeight: 600,
                fontSize: 12.5,
                padding: '7px 14px',
                borderRadius: 999,
                cursor: 'pointer',
              }}
            >
              Reintentar
            </button>
          </div>
        )}

        {!error && (datos?.items ?? []).map((n, i) => {
          const t = tono(tonoDeTexto(n.tono));
          return (
            <motion.button
              key={n.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...suave, delay: Math.min(i, 8) * 0.03 }}
              type="button"
              onClick={() => onIr(n)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                width: '100%',
                padding: '13px 16px',
                border: 0,
                borderBottom: `1px solid ${c.borde}`,
                background: n.leida ? c.panel : '#fbfdfc',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  flex: '0 0 32px',
                  borderRadius: 10,
                  display: 'grid',
                  placeItems: 'center',
                  background: t.bg,
                  color: t.fg,
                }}
              >
                <Icono nombre={n.icono} size={19} />
              </span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: c.texto }}>{n.titulo}</span>
                <span style={{ fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>{n.detalle}</span>
                <span style={{ fontSize: 12, color: c.texto4 }}>{n.tiempo}</span>
              </span>
              {!n.leida && (
                <span style={{ width: 8, height: 8, flex: '0 0 8px', borderRadius: 999, background: c.verde, marginTop: 6 }} />
              )}
            </motion.button>
          );
        })}

        {!error && datos && datos.items.length === 0 && (
          <div style={{ padding: '32px 20px', textAlign: 'center', fontSize: 13, color: c.texto2 }}>
            No hay novedades por ahora.
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', background: c.fondo, borderTop: `1px solid ${c.borde}`, fontSize: 12, color: c.texto3 }}>
        Se avisa por diferencias de descarga, OT nuevas y vencimientos de quincena.
      </div>
    </motion.div>
  );
}

export default function Layout({
  vista,
  titulo,
  onVista,
  perfil,
  onSalir,
  badges,
  sincronizado,
  children,
}: {
  vista: Vista;
  /** Reemplaza el título del encabezado cuando el genérico de la vista no alcanza (p. ej. la ficha de un pallet). */
  titulo?: string;
  onVista: (v: Vista, id?: string) => void;
  perfil: Perfil;
  onSalir: () => void;
  badges: Partial<Record<Vista, number>>;
  sincronizado: string;
  children: ReactNode;
}) {
  const [notisAbiertas, setNotisAbiertas] = useState(false);
  const [notis, setNotis] = useState<Notificaciones | null>(null);
  const [notisError, setNotisError] = useState(false);
  const [reintentoNotis, setReintentoNotis] = useState(0);
  const cajaNotis = useRef<HTMLDivElement>(null);

  // El cajón del celular reemplaza la tira de pestañas: los mismos grupos que
  // la barra lateral de la PC, pero plegados detrás de un botón.
  const [menuAbierto, setMenuAbierto] = useState(false);

  // Escanear o buscar un código desde cualquier pantalla: el operario lee la
  // etiqueta donde esté parado, no después de volver al inicio. Y el cliente
  // de ZF, que no tiene inicio, también.
  const [camara, setCamara] = useState(false);
  // De dónde se abrió la cámara: el escáner crece desde ese botón.
  const [origenCamara, setOrigenCamara] = useState<{ x: number; y: number }>();
  const abrirCamara = (e: EventoMouse<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigenCamara({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    setCamara(true);
  };
  const [buscando, setBuscando] = useState(false);
  // La caja de la lupa dentro del encabezado, como recorte: el buscador nace y muere ahí.
  const [origenLupa, setOrigenLupa] = useState('inset(0px 0px 0px 0px round 0px)');
  const [codigo, setCodigo] = useState('');
  const [hallazgo, setHallazgo] = useState<Hallazgo | null>(null);
  const [errorScan, setErrorScan] = useState('');
  const [hecho, setHecho] = useState('');

  async function resolver(leido: string) {
    const limpio = leido.trim().toUpperCase();
    if (!limpio) return;
    setCamara(false);
    setBuscando(false);
    setErrorScan('');
    setHecho('');
    try {
      setHallazgo(await api.buscar(limpio));
      setCodigo('');
    } catch (err) {
      setHallazgo(null);
      setErrorScan(mensajeDeError(err));
    }
  }

  const celular = useEsCelular();
  const hayEscaneo = !!(errorScan || hallazgo || hecho);
  function cerrarEscaneo() {
    setHallazgo(null);
    setErrorScan('');
    setHecho('');
  }

  // Lo leído es de la pantalla donde se leyó: al irse a otra, deja de estorbar.
  useEffect(cerrarEscaneo, [vista]);

  // Escape cierra el panel del escaneo, como el de notificaciones. Con la
  // cámara abierta, Escape es de la cámara.
  useEscape(hayEscaneo && !camara, cerrarEscaneo);

  /** `desnudo`: sin borde propio, porque va adentro de la píldora que es la lupa estirada. */
  const campoBusqueda = (autoFocus: boolean, desnudo = false) => (
    <input
      type="search"
      value={codigo}
      onChange={(e) => setCodigo(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void resolver(codigo);
        }
        if (e.key === 'Escape') setBuscando(false);
      }}
      placeholder="Pallet, capacho o nº de parte"
      aria-label="Buscar un código"
      autoFocus={autoFocus}
      style={{
        flex: 1,
        minWidth: 0,
        height: 40,
        padding: desnudo ? '0 6px' : '0 14px',
        border: desnudo ? 0 : `1px solid ${c.bordeFuerte}`,
        outline: desnudo ? 0 : undefined,
        borderRadius: 999,
        background: desnudo ? 'transparent' : c.panel,
        fontFamily: mono,
        fontSize: 14,
        color: c.texto,
      }}
    />
  );

  // La campana es lo que avisa lo que hizo otro: se refresca sola cada pocos
  // segundos, al cambiar de vista y al volver a la pestaña. Nadie va a estar
  // recargando la página para enterarse de que entró un pedido.
  const latido = useLatido(VIVO.rapido);

  useEffect(() => {
    let vigente = true;
    setNotisError(false);
    api
      .notificaciones()
      .then((d) => vigente && setNotis(d))
      .catch(() => vigente && setNotisError(true));
    return () => {
      vigente = false;
    };
  }, [vista, latido, reintentoNotis]);

  // Cierra el cajón del celular con Escape, igual que el panel de notificaciones.
  useEscape(menuAbierto, () => setMenuAbierto(false));

  // Cierra el panel al hacer clic afuera o con Escape.
  useEffect(() => {
    if (!notisAbiertas) return;
    const afuera = (e: MouseEvent) => {
      if (cajaNotis.current && !cajaNotis.current.contains(e.target as Node)) setNotisAbiertas(false);
    };
    document.addEventListener('mousedown', afuera);
    return () => document.removeEventListener('mousedown', afuera);
  }, [notisAbiertas]);
  useEscape(notisAbiertas, () => setNotisAbiertas(false));

  const badge = (v: Vista) => {
    const n = badges[v];
    return n === undefined || n === 0 ? undefined : miles(n);
  };

  // Una sola entrada para las tres etiquetas: abre la primera que el rol puede
  // ver y las otras quedan como pestañas (`PestanasEtiquetas`).
  const vistaEtiquetas = VISTAS_ETIQUETAS.find((v) => perfil.vistas.includes(v)) ?? 'etiquetasPallet';

  const grupos: Grupo[] = (
    [
    {
      titulo: 'General',
      items: [
        { vista: 'dashboard', label: 'Inicio', icono: 'home' },
        // Sin contador: era el total de pallets, no trabajo pendiente.
        { vista: 'stock', label: 'Stock disponible', icono: 'inventory_2' },
        { vista: 'solicitud', label: 'Solicitar OT', icono: 'assignment_add', badge: badge('solicitud') },
      ],
    },
    {
      titulo: 'Depósito',
      items: [
        // El contenedor primero: es por donde entra la mercadería, y lo que
        // declara es lo que después se arma.
        { vista: 'contenedores', label: 'Nuevo contenedor', icono: 'move_to_inbox', badge: badge('contenedores') },
        // La lista única de contenedores; la ficha de uno (vista 'contenedor') cuenta como estar acá.
        { vista: 'papeles', label: 'Contenedores', icono: 'description', tambien: ['contenedor'] },
        { vista: 'armado', label: 'Armado de pallets', icono: 'pallet', badge: badge('armado') },
        { vista: 'frac', label: 'Órdenes de trabajo', icono: 'assignment', badge: badge('frac') },
        { vista: 'correo', label: 'Pedidos por mail', icono: 'mail', badge: badge('correo') },
        // Vaciar rack se abre desde el panel del rack, en el mapa: sin entrada propia.
        { vista: 'racks', label: 'Mapa del depósito', icono: 'grid_view', tambien: ['vaciarRack'] },
        { vista: 'ubicar', label: 'Ubicar pallets', icono: 'open_with' },
        { vista: 'operar', label: 'Operar con escáner', icono: 'barcode_scanner' },
        { vista: vistaEtiquetas, label: 'Imprimir etiquetas', icono: 'barcode', tambien: VISTAS_ETIQUETAS },
        // Sin contador: no hay un endpoint barato con remitos pendientes.
        { vista: 'remitos', label: 'Remitos', icono: 'receipt_long' },
      ],
    },
    {
      titulo: 'Gestión',
      items: [
        { vista: 'resumen', label: 'Resumen', icono: 'monitoring' },
        // Sin contador: era el total en 2ª quincena, un dato de facturación,
        // no trabajo pendiente.
        { vista: 'estadias', label: 'Permanencia', icono: 'schedule' },
        { vista: 'inventario', label: 'Cargar inventario', icono: 'upload_file' },
        { vista: 'articulos', label: 'Artículos', icono: 'category' },
        { vista: 'archivos', label: 'Archivos compartidos', icono: 'folder_shared' },
        { vista: 'palletsEliminados', label: 'Pallets eliminados', icono: 'delete_history' },
      ],
    },
    {
      // Lo que se configura una vez, separado de lo que se usa a diario.
      titulo: 'Configuración',
      items: [
        { vista: 'planta', label: 'Planta del depósito', icono: 'architecture' },
        { vista: 'etiquetas', label: 'Diseño de etiquetas', icono: 'label' },
        { vista: 'usuarios', label: 'Usuarios y permisos', icono: 'group' },
        { vista: 'mantenimiento', label: 'Mantenimiento', icono: 'delete_sweep' },
        { vista: 'facturacion', label: 'Simulador de facturación', icono: 'calculate' },
      ],
    },
  ] as Grupo[])
    // Cada rol ve sólo lo suyo. Los grupos que quedan sin items desaparecen,
    // así el cliente de ZF no ve encabezados vacíos de la operación.
    .map((g) => ({ ...g, items: g.items.filter((it) => perfil.vistas.includes(it.vista)) }))
    .filter((g) => g.items.length > 0);

  // Con tres vistas sueltas, los encabezados de la operación no dicen nada.
  // Y ya no es sólo consulta: el buzón de archivos es de escribir.
  if (perfil.rol === 'operador_zf' && grupos.length > 0) {
    const items = grupos.flatMap((g) => g.items);
    grupos.length = 0;
    grupos.push({ titulo: 'Portal cliente', items });
  }

  // La campana no debe ofrecer una notificación que lleve a una vista que este
  // rol no tiene: sería un link cerrado en la cara.
  const notisVisibles = notis
    ? {
        noLeidas: notis.items.filter((n) => perfil.vistas.includes(n.vista as Vista) && !n.leida).length,
        items: notis.items.filter((n) => perfil.vistas.includes(n.vista as Vista)),
      }
    : null;

  const foto = (tam: number) => ({
    width: tam,
    height: tam,
    flex: `0 0 ${tam}px`,
    borderRadius: 999,
    border: `1px solid ${c.borde}`,
    backgroundColor: '#d7e6dd',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat' as const,
    backgroundImage: `url("${perfil.foto}")`,
  });

  async function irDesdeNoti(n: Notificacion) {
    setNotisAbiertas(false);
    onVista(n.vista as Vista);
    if (!n.leida) {
      await api.leerNotificacion(n.id).catch(() => {});
      setNotis((prev) =>
        prev
          ? {
              noLeidas: Math.max(0, prev.noLeidas - 1),
              items: prev.items.map((i) => (i.id === n.id ? { ...i, leida: true } : i)),
            }
          : prev,
      );
    }
  }

  async function leerTodas() {
    await api.leerTodas().catch(() => {});
    setNotis((prev) => (prev ? { noLeidas: 0, items: prev.items.map((i) => ({ ...i, leida: true })) } : prev));
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: c.fondo }}>
      <motion.aside layoutRoot
        id="wms-side"
        style={{
          width: 240,
          flex: '0 0 240px',
          background: c.panel,
          borderRight: `1px solid ${c.borde}`,
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        <div
          style={{
            flex: '0 0 64px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 16px',
            height: 64,
            borderBottom: `1px solid ${c.borde}`,
          }}
        >
          <Marca tamano={34} />
        </div>

        <nav
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            padding: '14px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {listaGrupos(grupos, vista, onVista, 'barra')}
        </nav>

        <div
          style={{
            flex: '0 0 auto',
            padding: 12,
            borderTop: `1px solid ${c.borde}`,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span role="img" aria-label={`Foto de perfil de ${perfil.nombre}`} style={foto(34)} />
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0, flex: 1 }}>
            <span
              style={{ fontSize: 13, fontWeight: 500, color: c.texto, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {perfil.nombre}
            </span>
            <span
              style={{ fontSize: 12, color: c.texto3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={perfil.mail}
            >
              {perfil.rolEtiqueta}
            </span>
          </span>
          <button
            type="button"
            onClick={onSalir}
            aria-label="Salir"
            title="Salir"
            style={{
              width: 34,
              height: 34,
              flex: '0 0 34px',
              borderRadius: 999,
              border: `1px solid ${c.borde}`,
              background: c.panel,
              color: c.texto2,
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Icono nombre="logout" size={19} />
          </button>
        </div>
      </motion.aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* En el celular cada píxel de ancho es para buscar y escanear: menos aire a los costados. */}
        <motion.header layoutRoot
          className="gap-2 px-3 tablet:gap-3 tablet:px-5"
          style={{
            height: 64,
            flex: '0 0 64px',
            background: c.panel,
            borderBottom: `1px solid ${c.borde}`,
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          {/* En el celular la barra lateral no está: este botón abre el mismo
              menú en un cajón lateral, en vez de la tira de pestañas de antes. */}
          <button
            type="button"
            className="solo-tactil"
            onClick={() => setMenuAbierto(true)}
            aria-label="Abrir menú"
            aria-expanded={menuAbierto}
            title="Abrir menú"
            style={botonRedondo(menuAbierto)}
          >
            <Icono nombre="menu" size={22} />
          </button>

          {/* En la tablet la barra lateral no está, así que el logo va acá. En
              el celular no: ahí el ancho es para que el título se lea entero,
              y la marca está en el cajón del menú. */}
          <img
            className="solo-tactil oculto-celular"
            src="/logo.png"
            alt="RDM Logística · ZF"
            width={30}
            height={24}
            style={{ flex: '0 0 30px' }}
          />

          <h1
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              color: c.texto,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {titulo ?? TITULOS[vista]}
          </h1>

          {/* El detalle de sincronización sólo cabe en escritorio */}
          <div
            className="solo-escritorio"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '0 12px',
              height: 38,
              flex: '0 0 auto',
              border: `1px solid ${c.borde}`,
              borderRadius: 999,
              background: c.fondo,
              color: c.texto3,
              fontSize: 13,
              minWidth: 0,
            }}
          >
            <Icono nombre="sync" size={18} />
            <span style={{ whiteSpace: 'nowrap' }}>Sincronizado · {sincronizado}</span>
          </div>

          {/* Buscar un código: en la PC el campo queda a la vista y resuelve
              con Enter; en el celular es un ícono que abre el campo encima
              del encabezado, porque ahí no entra al lado del título. */}
          <div className="solo-escritorio" style={{ display: 'flex', width: 260, flex: '0 1 260px', minWidth: 160 }}>
            {campoBusqueda(false)}
          </div>
          {/* Al tocar la lupa, el buscador se abre recortado desde la caja de
              la lupa hasta ocupar el encabezado, y al cerrar se encoge de vuelta
              hasta ella. No es un `layoutId`: con el encabezado pegado arriba,
              Motion lo remedía al cambiar de pantalla con la página scrolleada y
              la lupa volaba desde la mitad de la pantalla. */}
          <button
            type="button"
            className="solo-tactil"
            onClick={(e) => {
              const encabezado = e.currentTarget.closest('header')!.getBoundingClientRect();
              const lupa = e.currentTarget.getBoundingClientRect();
              setOrigenLupa(
                `inset(${lupa.top - encabezado.top}px ${encabezado.right - lupa.right}px ` +
                  `${encabezado.bottom - lupa.bottom}px ${lupa.left - encabezado.left}px round 999px)`,
              );
              setBuscando(true);
            }}
            aria-label="Buscar un código"
            title="Buscar un código"
            style={botonRedondo(false)}
          >
            <Icono nombre="search" size={21} />
          </button>
          <button
            type="button"
            onClick={abrirCamara}
            aria-label="Escanear con la cámara"
            title="Escanear con la cámara"
            style={botonRedondo(camara)}
          >
            <Icono nombre="photo_camera" size={21} />
          </button>
          <AnimatePresence>
            {buscando && (
              <motion.div
                key="buscador"
                className="solo-tactil"
                initial={{ clipPath: origenLupa }}
                animate={{ clipPath: 'inset(0px 0px 0px 0px round 0px)' }}
                exit={{ clipPath: origenLupa, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
                transition={deslizar}
                style={{
                  position: 'absolute',
                  inset: 0,
                  alignItems: 'center',
                  gap: 10,
                  padding: '0 16px',
                  background: c.panel,
                  zIndex: 1,
                }}
              >
                {/* La píldora es la lupa estirada; el campo de adentro se abre
                    desde el centro hacia los costados cuando ella ya llegó. */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    height: 44,
                    padding: '0 6px 0 14px',
                    border: `1px solid ${c.verde}`,
                    borderRadius: 999,
                    background: c.panel,
                  }}
                >
                  <span style={{ display: 'grid', color: c.verde }}>
                    <Icono nombre="search" size={21} />
                  </span>
                  <motion.div
                    {...abrir(999)}
                    transition={{ clipPath: { duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.12 }, opacity: { duration: 0.2, delay: 0.12 } }}
                    style={{ flex: 1, minWidth: 0, display: 'flex' }}
                  >
                    {campoBusqueda(true, true)}
                  </motion.div>
                </div>
                <motion.button
                  type="button"
                  onClick={() => setBuscando(false)}
                  aria-label="Cerrar la búsqueda"
                  initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: -90, scale: 0.6, transition: { duration: 0.18 } }}
                  transition={{ ...suave, delay: 0.18 }}
                  style={botonRedondo()}
                >
                  <Icono nombre="close" size={20} />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* En la tablet va sólo «Salir», sin la foto: el ancho es para buscar
              y escanear, que se usan mucho más. En el celular ni eso: «Salir»
              está al pie del cajón, y acá el título se cortaba en «Órd…». */}
          <span id="wms-user-hdr" className="oculto-celular" style={{ display: 'none', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
            <button
              type="button"
              onClick={onSalir}
              aria-label="Salir"
              title="Salir"
              style={{
                width: 38,
                height: 38,
                flex: '0 0 38px',
                borderRadius: 999,
                border: `1px solid ${c.borde}`,
                background: c.panel,
                color: c.texto2,
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Icono nombre="logout" size={19} />
            </button>
          </span>

          <div ref={cajaNotis} style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
            <button
              type="button"
              onClick={() => setNotisAbiertas((v) => !v)}
              aria-label="Notificaciones"
              aria-expanded={notisAbiertas}
              style={{
                position: 'relative',
                width: 38,
                height: 38,
                flex: '0 0 38px',
                borderRadius: 999,
                border: `1px solid ${notisAbiertas ? c.verde : c.borde}`,
                background: notisAbiertas ? c.verdeSuave : c.panel,
                color: notisAbiertas ? c.verde : c.texto2,
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Icono nombre="notifications" size={20} />
              {(notisVisibles?.noLeidas ?? 0) > 0 && (
                // La clave es el número: cada aviso nuevo hace latir el contador.
                <motion.span
                  key={notisVisibles!.noLeidas}
                  initial={{ scale: 0.4 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 520, damping: 18 }}
                  style={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    minWidth: 20,
                    height: 20,
                    padding: '0 5px',
                    borderRadius: 999,
                    background: c.rojo,
                    border: '2px solid #fff',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  {notisVisibles!.noLeidas}
                </motion.span>
              )}
            </button>

            <AnimatePresence>
              {notisAbiertas && (
                <PanelNotificaciones
                  datos={notisVisibles}
                  error={notisError}
                  onIr={irDesdeNoti}
                  onLeerTodas={leerTodas}
                  onReintentar={() => setReintentoNotis((n) => n + 1)}
                />
              )}
            </AnimatePresence>
          </div>
        </motion.header>

        {/* Cajón del celular: los mismos grupos que la barra lateral, detrás
            del botón ☰ del encabezado. Reemplaza la vieja tira de pestañas. */}
        <AnimatePresence>
        {menuAbierto && (
          <motion.div
            key="cajon"
            role="presentation"
            className="solo-tactil"
            onClick={() => setMenuAbierto(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3, delay: 0.05 } }}
            transition={{ duration: 0.3 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,26,.45)', zIndex: 30 }}
          >
            {/* El cajón se desliza con su propia curva; la sombra lo acompaña,
                así no se ve un borde que entra de golpe. */}
            <motion.nav layoutRoot
              aria-label="Menú"
              onClick={(e) => e.stopPropagation()}
              initial={{ x: '-100%', boxShadow: '0 0 0 rgba(26,26,26,0)' }}
              animate={{ x: 0, boxShadow: '0 0 32px rgba(26,26,26,.25)' }}
              exit={{ x: '-100%', boxShadow: '0 0 0 rgba(26,26,26,0)', transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
              transition={deslizar}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                width: 272,
                maxWidth: '82vw',
                background: c.panel,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: '10px 10px 16px',
                overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 14px' }}>
                <Marca tamano={30} />
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={() => setMenuAbierto(false)}
                  aria-label="Cerrar menú"
                  style={botonRedondo()}
                >
                  <Icono nombre="close" size={20} />
                </button>
              </div>
              {/* Las opciones llegan un instante después que el cajón, como
                  si vinieran detrás de él. */}
              <motion.div
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ ...suave, delay: 0.1 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                {listaGrupos(
                  grupos,
                  vista,
                  (v) => {
                    onVista(v);
                    setMenuAbierto(false);
                  },
                  'cajon',
                )}
              </motion.div>

              {/* Quién está adentro y «Salir», al pie del cajón como al pie de la
                  barra lateral de la PC: en el celular no entran en el encabezado. */}
              <div
                style={{
                  // Pegado al pie aunque el menú sea más largo que la pantalla:
                  // salir no puede depender de scrollear hasta el fondo.
                  position: 'sticky',
                  bottom: -16,
                  marginTop: 'auto',
                  padding: '12px 2px 16px',
                  background: c.panel,
                  borderTop: `1px solid ${c.borde}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span role="img" aria-label={`Foto de perfil de ${perfil.nombre}`} style={foto(34)} />
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: c.texto, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {perfil.nombre}
                  </span>
                  <span style={{ fontSize: 12, color: c.texto3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {perfil.rolEtiqueta}
                  </span>
                </span>
                <button type="button" onClick={onSalir} aria-label="Salir" title="Salir" style={botonRedondo()}>
                  <Icono nombre="logout" size={19} />
                </button>
              </div>
            </motion.nav>
          </motion.div>
        )}
        </AnimatePresence>

        <main className="w-full max-w-[1440px] flex-1 px-4 pt-4 pb-14 tablet:px-5 tablet:pt-6">
          <AnimatePresence>{camara && <Escaner key="camara" origen={origenCamara} onCerrar={() => setCamara(false)} onLeer={resolver} />}</AnimatePresence>
          {/* Cada pantalla entra subiendo un poco. La clave es la vista y su
              título: abrir otro pallet u otra orden también es pantalla nueva. */}
          <motion.div key={`${vista}·${titulo ?? ''}`} {...aparecer} exit={undefined}>
            {children}
          </motion.div>
        </main>

        {/* Lo leído flota encima de la pantalla actual, con el paso que sigue:
            abajo en el celular (a mano, como la bandeja de Ubicar) y en la
            esquina en la PC. Antes iba arriba de todo y había que subir a
            buscarlo desde donde se estaba trabajando. */}
        <AnimatePresence>
        {(errorScan || hallazgo || hecho) && (
          <motion.div
            key="escaneo"
            {...(celular ? subir : pop)}
            role="dialog"
            aria-label="Resultado del escaneo"
            style={{
              transformOrigin: 'bottom right',
              position: 'fixed',
              zIndex: 35,
              right: celular ? 0 : 20,
              left: celular ? 0 : undefined,
              bottom: celular ? 0 : 20,
              width: celular ? undefined : 460,
              maxHeight: celular ? '75vh' : 'calc(100vh - 104px)',
              overflowY: 'auto',
              background: c.panel,
              border: `1px solid ${c.borde}`,
              borderRadius: celular ? '16px 16px 0 0' : 16,
              boxShadow: '0 -6px 24px rgba(26,26,26,.16)',
              padding: '12px 16px',
              paddingBottom: celular ? 'calc(12px + env(safe-area-inset-bottom))' : 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icono nombre="barcode_scanner" size={19} color={c.texto3} />
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: c.texto }}>Escaneo</span>
              <button
                type="button"
                onClick={abrirCamara}
                style={{ ...botonSecundario, minHeight: 36, padding: '4px 12px', fontSize: 12.5 }}
              >
                <Icono nombre="photo_camera" size={17} />
                Escanear otro
              </button>
              <button type="button" onClick={cerrarEscaneo} aria-label="Cerrar" style={{ ...botonRedondo(), width: 36, height: 36, flex: '0 0 36px' }}>
                <Icono nombre="close" size={18} />
              </button>
            </div>
            {errorScan && <PanelError mensaje={errorScan} />}
            {hecho && (
              <div role="status" style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icono nombre="check_circle" size={20} color={c.verde} />
                <span style={{ flex: 1 }}>{hecho}</span>
              </div>
            )}
            {hallazgo && (
              <ResultadoEscaneo
                hallazgo={hallazgo}
                vistas={perfil.vistas}
                onIr={(v, id) => {
                  cerrarEscaneo();
                  onVista(v, id);
                }}
                onEliminado={(mensaje) => {
                  setHallazgo(null);
                  setHecho(mensaje);
                }}
              />
            )}
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </div>
  );
}
